import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Flip } from '../../services/flipsService';

interface FlipDetailPanelProps {
  open: boolean;
  onClose: () => void;
  flip: Flip | null;
}

// Same shape as the flipper's History tab: integer amounts, k/M above 1000.
const formatNumber = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(n)) {
    return 'n/a';
  }
  const v = Math.round(n);
  if (Math.abs(v) >= 1000000) {
    return `${(v / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(v) >= 1000) {
    return `${(v / 1000).toFixed(1)}k`;
  }
  return String(v);
};

const formatPct = (v: number | null | undefined): string => {
  if (v == null || Number.isNaN(v)) {
    return 'n/a';
  }
  return `${(v * 100).toFixed(1)}%`;
};

// A leg is a planned hop when it sits on a consecutive pair of the planned path;
// anything else is the executor adjusting (selling an underfill back, routing home).
const isPlannedHop = (path: string[], have: string, want: string): boolean => {
  for (let i = 0; i < path.length - 1; i += 1) {
    if (path[i] === have && path[i + 1] === want) {
      return true;
    }
  }
  return false;
};

const FlipDetailPanel: React.FC<FlipDetailPanelProps> = ({ open, onClose, flip }) => {
  if (!flip) {
    return null;
  }

  const planned = (flip.expectedGross ?? 0) !== 0 || (flip.expectedNet ?? 0) !== 0 || (flip.expectedGold ?? 0) !== 0;
  const path = flip.path ?? [];
  const legs = flip.legs ?? [];
  const commit = flip.commitQty ?? 0;
  const deployed = flip.sourceSpent ?? 0;

  const planRows = [
    { label: `Spent (${flip.source})`, expected: flip.commitQty, actual: flip.sourceSpent, cost: false },
    { label: `Returned (${flip.source})`, expected: flip.expectedGross ?? 0, actual: flip.sourceRecovered, cost: false },
    { label: `Net (${flip.source})`, expected: flip.expectedNet ?? 0, actual: flip.netSource, cost: false },
    { label: 'Gold', expected: flip.expectedGold ?? 0, actual: flip.goldSpent, cost: true },
  ];

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '100%',
          padding: 2,
          overflow: 'auto',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h5' component='h2'>
          Flip Details
        </Typography>
        <IconButton onClick={onClose} aria-label='close'>
          <CloseIcon />
        </IconButton>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant='h6' component='span'>
            {flip.characterName}
          </Typography>
          <Typography variant='body2' component='span' color='text.secondary'>
            {new Date(flip.flippedAt).toLocaleString()}
          </Typography>
          <Typography
            variant='body2'
            component='span'
            sx={{ color: flip.result === 'done' ? 'success.main' : 'warning.main', textTransform: 'capitalize' }}
          >
            {flip.result}
          </Typography>
          {flip.reason !== '' && (
            <Typography variant='body2' component='span' color='text.secondary'>
              ({flip.reason})
            </Typography>
          )}
        </Box>
        <Typography variant='body1' sx={{ mt: 1 }}>
          {path.length > 0 ? path.join(' -> ') : 'no plan recorded'}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Committed {formatNumber(flip.commitQty)} {flip.source} | ran for {(flip.durationMs / 1000).toFixed(0)}s
        </Typography>
      </Paper>

      <Typography variant='h6' gutterBottom>
        Plan against outcome
      </Typography>
      {!planned && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Resumed cycle: its entry plan was never recorded, so only the measured side below is real.
        </Typography>
      )}
      {planned && (
        <TableContainer component={Paper} elevation={2} sx={{ mb: 2 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align='right'>Expected</TableCell>
                <TableCell align='right'>Actual</TableCell>
                <TableCell align='right'>Off by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planRows.map(row => {
                // Gold is a cost, so spending less than planned is the good direction.
                const delta = row.cost ? row.expected - row.actual : row.actual - row.expected;
                const scale = Math.abs(row.expected);
                const drift = scale < 1e-9 ? formatNumber(delta) : `${formatNumber(delta)} (${((delta / scale) * 100).toFixed(0)}%)`;
                return (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align='right'>{formatNumber(row.expected)}</TableCell>
                    <TableCell align='right'>{formatNumber(row.actual)}</TableCell>
                    <TableCell align='right' sx={{ color: delta >= 0 ? 'success.main' : 'error.main' }}>
                      {drift}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {planned && commit > 0 && deployed < commit * 0.98 && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Only {formatNumber(deployed)} of the {formatNumber(commit)} committed was deployed, so the expected column above, which was sized
          for the whole commitment, is not a like-for-like comparison. Judge this cycle on Net.
        </Typography>
      )}

      <Typography variant='h6' gutterBottom>
        Execution
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Ordered and Filled read in the same direction as Hop, spent to received. Ordered is what the order asked for; Filled is what
        actually moved. A recovery step is the executor adjusting: selling an underfill back, or routing home a currency the plan never
        mentioned.
      </Typography>
      {legs.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No execution legs recorded.
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Hop</TableCell>
                <TableCell>Step</TableCell>
                <TableCell align='right'>Ordered</TableCell>
                <TableCell align='right'>Filled</TableCell>
                <TableCell align='right'>Fill</TableCell>
                <TableCell align='right'>Took</TableCell>
                <TableCell>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {legs.map((leg, index) => {
                const measured = leg.measured !== false;
                const ratio = measured && leg.wantQty > 0 ? leg.received / leg.wantQty : 0;
                const outcome = !measured ? 'unconfirmed' : leg.cancelled ? 'cancelled' : 'filled';
                return (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{`${leg.have} -> ${leg.want}`}</TableCell>
                    <TableCell sx={{ color: isPlannedHop(path, leg.have, leg.want) ? 'text.secondary' : 'warning.main' }}>
                      {isPlannedHop(path, leg.have, leg.want) ? 'planned' : 'recovery'}
                    </TableCell>
                    <TableCell align='right'>{`${formatNumber(leg.haveQty)} -> ${formatNumber(leg.wantQty)}`}</TableCell>
                    <TableCell align='right'>
                      {measured ? `${formatNumber(leg.spent)} -> ${formatNumber(leg.received)}` : 'not measured'}
                    </TableCell>
                    <TableCell align='right' sx={{ color: measured && ratio >= 0.999 ? 'success.main' : 'warning.main' }}>
                      {measured ? formatPct(ratio) : '?'}
                    </TableCell>
                    <TableCell align='right'>{(leg.durationMs ?? 0) / 1000}s</TableCell>
                    <TableCell sx={{ color: !measured ? 'text.secondary' : leg.cancelled ? 'warning.main' : 'success.main' }}>
                      {outcome}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Drawer>
  );
};

export default FlipDetailPanel;
